import React, { useMemo, useState } from 'react';
import { useNotifications } from './NotificationContext';
import { useLocation } from 'react-router-dom';
import { CheckAll, Trash3, XLg } from 'react-bootstrap-icons';

// Mesma paleta usada nos badges de status da SideBar (Menu.scss .badge-status)
// — reaproveitada aqui pra manter a identidade visual e o código de cor por severidade.
const typeMeta = (t) => {
  switch (t) {
    case 'error':
    case 'bloqueio':
    case 'critical':
      return { border: '#ef4444', bg: 'rgba(239,68,68,0.22)', tag: '#fecaca' };
    case 'warn':
    case 'warning':
      return { border: '#fd7e14', bg: 'rgba(253,126,20,0.22)', tag: '#fed7aa' };
    case 'success':
      return { border: '#22c55e', bg: 'rgba(34,197,94,0.22)', tag: '#bbf7d0' };
    default:
      return { border: '#0ea5e9', bg: 'rgba(14,165,233,0.22)', tag: '#bae6fd' };
  }
};

const SIDEBAR_GRADIENT = 'linear-gradient(180deg, #022c22 0%, #064e3b 45%, #022c22 100%)';

const headerIconBtnStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.25)',
  color: '#e8f5e9',
  width: 32,
  height: 32,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export function NotificationBell() {
  const { items } = useNotifications();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const unread = useMemo(() => items.filter((i) => !i.read).length, [items]);

  const path = location.pathname.toLowerCase();

  const isPublicRoute =
    path.includes('/menu/') || // Ex: /menu/:empresa, /menu/:empresa/pedidos, etc.
    path.includes('/loja/') || // Ex: /loja/:empresa, /loja/:empresa/pedidos, etc.
    path.includes('/auth/login') || // Ex: /:cliente/auth/login
    path.includes('/mesa/') ||
    path === '/' || // Se for a rota inicial (searchUrl ou dashboard dependendo da lógica)
    path === '/login';

  // Autoatendimento (tablet do cliente) e chamada de senhas (monitor): telas de exibição, sem sino.
  const isAdminRoute =
    path.includes('/painel') ||
    path.includes('/admin') ||
    path.includes('/pdv') ||
    path.includes('/autoatendimento') ||
    path.includes('/chamada-senhas');

  // Se for rota pública ou rota administrativa, não renderiza o sininho.
  if (isPublicRoute || isAdminRoute) {
    return null;
  }

  return (
    <>
      {/* Botão Flutuante */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn btn-light shadow position-fixed"
        style={{
          bottom: 16, // antes era "top: 16"
          right: 16,
          zIndex: 1051,
          borderRadius: 999,
          padding: '10px 14px',
        }}
        aria-label="Notificações"
      >
        <span className="position-relative">
          🔔
          {!!unread && (
            <span
              className="position-absolute translate-middle badge rounded-pill bg-danger"
              style={{ top: -6, right: -10 }}
            >
              {unread}
            </span>
          )}
        </span>
      </button>

      <NotificationPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function NotificationPanel({ open, onClose }) {
  const { items, markRead, markAllRead, remove, clearNonSticky } = useNotifications();
  const location = useLocation();

  // Também não renderiza o painel no painel administrativo
  const path = location.pathname.toLowerCase();
  if (path.includes('/painel') || path.includes('/admin')) {
    return null;
  }

  return (
    <>
      {/* backdrop */}
      {open && (
        <div
          className="position-fixed w-100 h-100"
          style={{ inset: 0, background: 'rgba(0,0,0,.25)', zIndex: 1050 }}
          onClick={onClose}
        />
      )}

      {/* painel lateral */}
      <div
        className="position-fixed shadow-lg"
        style={{
          top: 0,
          right: 0,
          width: 380,
          height: '100dvh',
          transform: open ? 'translateX(0)' : 'translateX(110%)',
          transition: 'transform .25s ease',
          zIndex: 1051,
          display: 'flex',
          flexDirection: 'column',
          background: SIDEBAR_GRADIENT,
        }}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="d-flex align-items-center justify-content-between px-3 py-3"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }}
        >
          <div className="fw-semibold d-flex align-items-center gap-2" style={{ color: '#e8f5e9' }}>
            <span style={{ fontSize: 18 }}>🔔</span> Notificações
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm p-0"
              style={headerIconBtnStyle}
              title="Marcar todas como lidas"
              onClick={markAllRead}
            >
              <CheckAll size={16} />
            </button>
            <button
              type="button"
              className="btn btn-sm p-0"
              style={headerIconBtnStyle}
              title="Limpar não-fixas"
              onClick={clearNonSticky}
            >
              <Trash3 size={14} />
            </button>
            <button
              type="button"
              className="btn btn-sm p-0"
              style={headerIconBtnStyle}
              title="Fechar"
              onClick={onClose}
            >
              <XLg size={14} />
            </button>
          </div>
        </div>

        <div className="p-2 overflow-auto" style={{ flex: 1 }}>
          {items.length === 0 && (
            <div className="text-center py-5" style={{ color: '#9ca3af' }}>
              Sem notificações.
            </div>
          )}

          {items.map((n) => {
            const meta = typeMeta(n.tipo);
            return (
              <div
                key={n.id}
                className="mb-2 rounded-3"
                style={{
                  background: meta.bg,
                  border: `1px solid ${meta.border}`,
                  borderRight: `4px solid ${meta.border}`,
                }}
              >
                <div className="p-3">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <strong style={{ color: '#e8f5e9' }}>{n.titulo}</strong>
                    <span
                      className="badge"
                      style={{ background: 'rgba(255,255,255,0.12)', color: meta.tag }}
                    >
                      {n.remetente}
                    </span>
                    {!n.read && (
                      <span className="badge" style={{ background: '#22c55e', color: '#022c22' }}>
                        Novo
                      </span>
                    )}
                    {n.sticky && (
                      <span
                        className="badge"
                        style={{ background: 'rgba(255,255,255,0.15)', color: '#e8f5e9' }}
                      >
                        Fixa
                      </span>
                    )}
                  </div>
                  <div className="small mt-1" style={{ color: '#9ca3af' }}>
                    {n.date.toLocaleString('pt-BR')}
                  </div>

                  <div className="mt-2" style={{ color: '#ecfdf5' }}>
                    {n.texto}
                  </div>

                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {!n.read && (
                      <button
                        className="btn btn-sm"
                        style={{ border: '1px solid rgba(255,255,255,0.25)', color: '#e8f5e9' }}
                        onClick={() => markRead(n.id, true)}
                      >
                        Marcar lida
                      </button>
                    )}
                    <button
                      className="btn btn-sm"
                      style={{ border: '1px solid #ef4444', color: '#fecaca' }}
                      onClick={() => remove(n.id)}
                    >
                      Remover
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
